// ID format helpers per docs/plan/02-data-model.md §ID 규칙.
//
// Robot          #NNN              "#123"
// Lead (legacy)  LEAD-YYYYMMDDNN   "LEAD-2026042101"  ← was the §02 ID
//                                                       before the entity
//                                                       was renamed Deal
// Deal           DEAL-YYYYMMDDNN   "DEAL-2026042101"  Phase 5 / v1.1
// Contract       CNT-YYYY-NNNN     "CNT-2026-0001"    Phase 5 / v1.1
//                                                     (formerly the
//                                                     YYYYMMDDNN style
//                                                     in the legacy
//                                                     accounting Sheet)
// Firmware       vYYYYMMDD         "v20260421"
// CustomerReport RPT-YYYYMMDD-NNN  "RPT-20260421-007"
// QR             QR-#NNN-{rev}     "QR-#123-2"
const NN = (n) => String(n).padStart(2, '0');
const NNN = (n) => String(n).padStart(3, '0');
export function formatRobotShortId(serialShort) {
    return `#${serialShort}`;
}
/** Short SN 이 없는 로봇을 알림 문구에 적을 때 쓰는 말. */
export const ROBOT_SHORT_SN_MISSING_LABEL = '(Short SN 미등록)';
/**
 * 알림(슬랙 · 콘솔 종)에 찍는 로봇 이름표 — **Short SN 만** 쓴다("#123").
 *
 * 2026-11-04 오너 지시 — 슬랙 설치 완료 알림에 `로봇: cmqze2vu` 처럼 로봇 id 앞자리가 찍혔다.
 * Short SN 이 비면 로봇 id · Long SN 으로 메우지 않고, 비었다고 그대로 말한다.
 */
export function robotShortLabel(serialShort) {
    const short = serialShort?.trim();
    return short ? formatRobotShortId(short) : ROBOT_SHORT_SN_MISSING_LABEL;
}
export function parseRobotShortId(raw) {
    const match = /^#(\d+)$/.exec(raw.trim());
    // `match[1]` is `string | undefined` under noUncheckedIndexedAccess
    // even though the regex guarantees the capture group resolves when
    // the overall match succeeds. `?? null` keeps the return type
    // narrow and matches the documented contract.
    return match ? (match[1] ?? null) : null;
}
export function formatLeadId(date, seq) {
    const y = date.getFullYear();
    const m = NN(date.getMonth() + 1);
    const d = NN(date.getDate());
    return `LEAD-${y}${m}${d}${NN(seq)}`;
}
export function formatContractId(date, seq) {
    const y = date.getFullYear();
    const m = NN(date.getMonth() + 1);
    const d = NN(date.getDate());
    return `${y}${m}${d}${NN(seq)}`;
}
export function formatFirmwareId(date) {
    const y = date.getFullYear();
    const m = NN(date.getMonth() + 1);
    const d = NN(date.getDate());
    return `v${y}${m}${d}`;
}
export function formatCustomerReportId(date, seq) {
    const y = date.getFullYear();
    const m = NN(date.getMonth() + 1);
    const d = NN(date.getDate());
    return `RPT-${y}${m}${d}-${NNN(seq)}`;
}
export function formatQrId(serialShort, rev) {
    return `QR-#${serialShort}-${rev}`;
}
// --- Phase 5 (v1.1) Deal / Contract humanId helpers --------------------
const NNNN = (n) => String(n).padStart(4, '0');
/**
 * Phase 5 Deal entity humanId — `DEAL-YYYYMMDDNN`. The `seq` is a
 * per-day counter inside the calling service's transaction; format
 * is identical to the legacy `formatLeadId` except for the prefix.
 *
 * 한국시간 통일 4차(26-08-14) — 인자가 `Date` 였을 때 이 함수는 서버 시계
 * (`getFullYear/getMonth/getDate`)로 날짜를 읽었다. 운영 컨테이너에는 TZ 가
 * 없어 그 시계는 **UTC** 다 → 한국 오전 9시 전에 만든 딜이 `DEAL-2026081300`
 * 처럼 **어제 번호**를 받았다. 이제 KST 하루 라벨을 **받아서** 찍는다:
 * 호출부가 `kstDayRangeUtc(now)` 하나에서 라벨과 채번 구간을 같이 꺼내므로
 * "찍는 날짜"와 "세는 하루"가 구조적으로 어긋날 수 없다.
 *
 * @param kstDayLabel `kstDayRangeUtc(...).label` — `"YYYY-MM-DD"`(KST).
 */
export function formatDealHumanId(kstDayLabel, seq) {
    return `DEAL-${kstDayLabel.replace(/-/g, '')}${NN(seq)}`;
}
/**
 * Phase 5 Contract entity humanId — `CNT-YYYY-NNNN`. The `seq` is a
 * per-year counter inside the calling service's transaction. The 4-digit
 * format gives 9999 contracts/year of headroom; v1.1 fleet ramp peaks
 * at <200 contracts/year so it's plenty.
 */
export function formatContractHumanId(year, seq) {
    return `CNT-${year}-${NNNN(seq)}`;
}
//# sourceMappingURL=ids.js.map